import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../constants';

/* Fictitious logos — SVG inline per donatori senza brand_logo_url */
const FictitiousLogo = ({ name, color }) => {
  const shapes = [
    /* Diamond */ (c) => <><rect x="6" y="6" width="20" height="20" rx="3" fill={c} transform="rotate(45 16 16)" /><circle cx="16" cy="16" r="4" fill="#050505" /></>,
    /* Hexagon */ (c) => <><polygon points="16,3 28,10 28,22 16,29 4,22 4,10" fill={c} opacity="0.9" /><polygon points="16,8 23,12 23,20 16,24 9,20 9,12" fill="#050505" /></>,
    /* Circle + Cross */ (c) => <><circle cx="16" cy="16" r="12" fill={c} /><line x1="10" y1="16" x2="22" y2="16" stroke="#050505" strokeWidth="2.5" /><line x1="16" y1="10" x2="16" y2="22" stroke="#050505" strokeWidth="2.5" /></>,
    /* Triangle */ (c) => <><polygon points="16,3 30,28 2,28" fill={c} /><polygon points="16,10 24,25 8,25" fill="#050505" /></>,
    /* Star burst */ (c) => <><circle cx="16" cy="16" r="11" fill={c} /><text x="16" y="21" textAnchor="middle" fill="#050505" fontSize="14" fontFamily="monospace" fontWeight="900">{name.charAt(0)}</text></>,
  ];
  const idx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % shapes.length;
  return (
    <svg viewBox="0 0 32 32" className="w-full h-full">
      {shapes[idx](color)}
    </svg>
  );
};

const WallOfFame = () => {
  const [donors, setDonors] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/donations/wall`)
      .then(r => r.json())
      .then(data => { setDonors(Array.isArray(data) ? data : []); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded || donors.length === 0) return null;

  // Only first 3
  const top3 = donors.slice(0, 3);

  return (
    <section data-testid="wall-of-fame" className="py-24 border-t border-white/10">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FF41] mb-1">&lt;/ Wall of Fame</p>
          <p className="text-[9px] font-mono text-zinc-600 tracking-[0.2em] mb-4">DIGITAL HUB SOCIALE</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            I nostri <span className="text-[#00FF41]">Fondatori</span>
          </h2>
          <p className="text-white font-bold max-w-xl mx-auto mb-3">Chi ha creduto nel progetto fin dall'inizio.</p>
          <p className="text-white text-xs font-mono font-bold flex items-center justify-center gap-2">In collaborazione con <a href="https://www.schedabusiness.com" target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-110 transition-transform"><img src="/logo_aicovo_transparent.png" alt="AI_covo.tech" className="h-8 w-auto inline-block" /></a></p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {top3.map((d, i) => {
            const logoSrc = d.company_name?.toLowerCase().includes('schedabusiness') ? 'https://customer-assets.emergentagent.com/job_4f095f05-6ea5-4cfc-bbce-0ff88cbd6553/artifacts/j1n3vtnb_chrome_XYTQH9aFwa.png' : d.brand_logo_url;
            return (
              <div
                key={i}
                data-testid={`wall-donor-${i}`}
                className="group bg-[#0A0A0A] border border-zinc-800 hover:border-opacity-100 p-6 text-center transition-all duration-300 hover:scale-[1.03] rounded-xl"
                style={{ borderColor: d.belt_color ? `${d.belt_color}33` : 'rgba(255,255,255,0.08)' }}
              >
                {/* Logo */}
                <div className={`mx-auto mb-4 overflow-hidden border-2 flex items-center justify-center ${logoSrc ? 'w-28 h-28 rounded-xl bg-white' : 'w-20 h-20 rounded-full bg-black'}`}
                  style={{ borderColor: d.belt_color || '#333' }}>
                  {logoSrc ? (
                    <img src={logoSrc} alt={d.company_name || d.name} className="w-full h-full object-contain p-1" />
                  ) : d.name === 'Anonimo' ? (
                    <svg className="w-10 h-10 text-zinc-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  ) : (
                    <FictitiousLogo name={d.name} color={d.belt_color || '#00FF41'} />
                  )}
                </div>

                {/* Name */}
                <p className="font-bold text-white text-base truncate mb-1"
                  title={d.name === 'Anonimo' ? 'Donatore Anonimo' : d.name}>
                  {d.name === 'Anonimo' ? (
                    <span className="text-zinc-500 italic">Anonimo</span>
                  ) : d.company_name ? (
                    d.company_name
                  ) : (
                    d.name
                  )}
                </p>

                {/* Belt */}
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  <div className="w-5 h-2 rounded-sm" style={{ background: d.belt_color || '#555' }} />
                  <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: d.belt_color || '#666' }}>
                    {d.belt}
                  </span>
                </div>

                {/* Amount */}
                <p className="font-mono font-black text-2xl mt-3" style={{ color: d.belt_color || '#00FF41' }}>
                  {d.amount}&euro;
                </p>

                {d.comune && (
                  <p className="text-zinc-600 text-[10px] font-mono mt-1 truncate">{d.comune}</p>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <Link to="/dona" data-testid="wall-cta-dona" className="inline-block font-mono font-black text-base text-[#00FF41] border-2 border-[#00FF41] px-8 py-3 rounded-lg hover:bg-[#00FF41] hover:text-black transition-all">
            CONTINUA &rarr;
          </Link>
          <Link to="/chiantibanca" data-testid="wall-cta-banche" className="inline-block font-mono font-bold text-sm text-[#D4A574] border border-[#D4A574]/40 px-6 py-3 rounded-lg hover:bg-[#D4A574]/10 transition-all">
            Strategia Banche &amp; Fondazioni
          </Link>
        </div>
        <p className="text-center mt-6">
          <span className="inline-block bg-[#00FF41]/10 border border-[#00FF41]/30 rounded-full px-5 py-1.5 text-[#00FF41] font-mono text-xs font-bold uppercase tracking-wider">
            Deducibile 100% Formazione
          </span>
        </p>
      </div>
    </section>
  );
};

export default WallOfFame;
